import type {
  Address,
  AgendaItem,
  AgendaModule,
  Document,
  LetterHead,
  Meeting,
  NumberFormat,
  Team,
  TeamMemberWContact,
  Tenant,
  User,
} from "@repo/types";
import {
  Document as PDFDocument,
  Font,
  Image,
  Link,
  Page,
  StyleSheet,
  Text,
  View,
  Svg,
  Path,
  type SVGProps,
} from "@react-pdf/renderer";
import { arrayify, formatTZ } from "@repo/shared/utils";
import { loadFonts } from "../fonts";
import { createContext, useContext } from "react";
import {
  coerceLayout,
  coerceTheme,
  formatLineIndex,
  getIconStyles,
  getStyles,
  type AgendaTheme,
  type LayoutType,
  type StyleProperties,
} from "./agendaLayoutsThemes";

// #region Prop Interface

export interface AgendaPreviewProps {
  tenant?: Tenant;
  meeting?: Meeting;
  team?: Team;
  letterHead?: LetterHead;
  enableLinks?: boolean | "all";
  apiUrl: string;
  substituteLinkMap?: Record<string, string>;
  userMap?: Record<string, User>;
  augmented?: boolean;
  numberFormat?: NumberFormat;
}

interface AgendaDocumentContextType {
  tenant?: Tenant;
  meeting?: Meeting;
  team?: Team;
  letterHead?: LetterHead;
  enableLinks: boolean | "all";
  apiUrl: string;
  substituteLinkMap: Record<string, string>;
  userMap?: Record<string, User>;
  augmented: boolean;
  sectionNumbering: NumberFormat;
  itemNumbering: NumberFormat;
  documentNumbering: NumberFormat;
  nestNumbering: boolean;
  // additional
  timeZone: string;
  theme: AgendaTheme;
  layout: LayoutType;
}

// #endregion

// #region Utilities

// fonts

let fontsLoaded = false;
const readyPromise = loadFonts().then((fonts) => {
  fontsLoaded = true;

  Font.register({
    family: "Inter",
    fonts: [
      { src: fonts.sansNormal },
      { src: fonts.sansBold, fontWeight: 700 },
      { src: fonts.sansNormalItalic, fontStyle: "italic" },
      { src: fonts.sansBoldItalic, fontWeight: 700, fontStyle: "italic" },
    ],
  });
});

Font.registerHyphenationCallback((word) => [word]);

// styles

const styles = StyleSheet.create({
  page: {
    backgroundColor: "#FFFFFF",
    padding: 26,
    fontFamily: "Inter",
    fontSize: 12,
  },
  titleSection: {},
  meetingDate: {
    margin: 0,
    alignItems: "center",
  },
  meetingText: {
    margin: 0,
    alignItems: "center",
    fontSize: 10,
  },
  section: {
    margin: 10,
    padding: 10,
    flexGrow: 1,
    backgroundColor: "#F5F5F5",
  },
});

// icons

const makeIcon = (pathData: string) => (props: SVGProps) => (
  <Svg width={16} height={16} {...props} viewBox="0 0 72 72">
    <Path fill={props.color ?? "black"} d={pathData} />
  </Svg>
);

const IconAttachment = makeIcon(
  "M56.231,64.856c-2.983,0-5.966-1.136-8.236-3.406L21.616,35.071c-1.395-1.395-2.163-3.248-2.163-5.22s0.769-3.825,2.164-5.219c1.393-1.394,3.247-2.163,5.22-2.163s3.827,0.769,5.221,2.164l22.846,22.846c0.977,0.977,0.977,2.559,0,3.535s-2.559,0.977-3.535,0L28.521,28.168c-0.9-0.9-2.47-0.9-3.37,0c-0.451,0.451-0.699,1.048-0.699,1.684c0,0.636,0.248,1.234,0.698,1.685L51.53,57.915c2.593,2.592,6.811,2.591,9.401,0c2.592-2.592,2.592-6.81,0-9.401l-33.181-33.18c-4.255-4.254-11.178-4.254-15.433,0c-2.061,2.061-3.196,4.801-3.196,7.716c0,2.914,1.135,5.654,3.196,7.715l25.974,25.974c0.977,0.977,0.977,2.559,0,3.535s-2.56,0.977-3.535,0L8.783,34.301c-3.006-3.005-4.661-7-4.661-11.25s1.655-8.246,4.661-11.251c6.205-6.204,16.3-6.204,22.503,0l33.181,33.18c4.542,4.541,4.542,11.931,0,16.472C62.196,63.721,59.214,64.856,56.231,64.856z"
);
const IconConfidential = makeIcon(
  "M55.214,31.448h-3.506v-7.957c0-8.661-7.045-15.707-15.707-15.707c-8.659,0-15.704,7.046-15.704,15.707v7.957h-3.508c-2.096,0-3.795,1.699-3.795,3.794v25.18c0,2.096,1.699,3.795,3.795,3.795h38.425c2.094,0,3.793-1.699,3.793-3.795v-25.18C59.007,33.147,57.308,31.448,55.214,31.448z M36,55.486c-2.41,0-4.367-1.959-4.367-4.369c0-1.686,0.967-3.133,2.367-3.86v-4.194c0-1.104,0.896-2,2-2s2,0.896,2,2v4.193c1.4,0.729,2.366,2.176,2.366,3.861C40.366,53.527,38.413,55.486,36,55.486z M44.12,31.448H27.885v-7.957c0-4.477,3.643-8.119,8.116-8.119c4.477,0,8.119,3.643,8.119,8.119V31.448z"
);
const IconBrokenLink = makeIcon(
  "M39.909,43.598c1.095,1.096,1.095,2.873,0,3.968l-10.25,10.25c-2.066,2.065-4.815,3.202-7.736,3.202c0,0,0,0-0.004,0c-2.921,0-5.669-1.137-7.733-3.202c-2.071-2.065-3.21-4.813-3.206-7.739c0-2.922,1.138-5.668,3.206-7.74l10.248-10.248c1.095-1.095,2.871-1.095,3.966,0s1.095,2.869,0,3.964l-10.25,10.25c-1.008,1.01-1.564,2.349-1.564,3.773c0,1.424,0.556,2.767,1.564,3.773c2.079,2.082,5.465,2.082,7.545,0l10.25-10.252C37.041,42.502,38.814,42.502,39.909,43.598z M21.834,24.805c0.409,0.411,0.947,0.619,1.486,0.619c0.54,0,1.076-0.208,1.487-0.619c0.821-0.821,0.821-2.151,0-2.974l-5.131-5.124c-0.821-0.823-2.152-0.823-2.973,0c-0.821,0.821-0.821,2.151,0,2.974L21.834,24.805z M28.23,23.39L28.23,23.39c1.16-0.004,2.102-0.945,2.102-2.105l-0.005-7.25c0-1.162-0.942-2.104-2.102-2.104l0,0c-1.162,0-2.104,0.942-2.104,2.104l0.005,7.255C26.126,22.45,27.068,23.39,28.23,23.39z M23.39,28.227c0-1.16-0.942-2.102-2.104-2.102h-7.251c-1.164,0-2.104,0.942-2.104,2.102c0,1.164,0.942,2.102,2.104,2.102h7.251C22.448,30.329,23.39,29.387,23.39,28.227z M50.168,47.195c-0.82-0.82-2.152-0.82-2.973,0c-0.821,0.821-0.821,2.152,0,2.974l5.125,5.128c0.41,0.411,0.949,0.617,1.487,0.617c0.536,0,1.075-0.206,1.487-0.617c0.82-0.821,0.82-2.152,0-2.974L50.168,47.195z M57.969,41.671h-7.256l0,0c-1.16,0-2.102,0.938-2.102,2.101c0,1.163,0.941,2.105,2.102,2.105h7.25c0.004,0,0.004,0,0.004,0c1.16,0,2.102-0.94,2.102-2.101C60.068,42.613,59.131,41.671,57.969,41.671z M43.775,48.611L43.775,48.611c-1.162,0-2.104,0.939-2.104,2.099l-0.006,7.253c0,1.164,0.942,2.105,2.104,2.105l0,0c1.16,0,2.103-0.938,2.103-2.1l0.005-7.253C45.878,49.554,44.936,48.611,43.775,48.611z M36.054,28.4l10.25-10.25c2.082-2.08,5.466-2.08,7.546,0c2.078,2.081,2.078,5.467,0,7.545l-10.248,10.25c-1.096,1.095-1.096,2.869,0,3.964c0.546,0.549,1.264,0.82,1.98,0.82c0.719,0,1.435-0.271,1.982-0.82l10.25-10.25c4.266-4.265,4.266-11.206,0-15.477c-4.271-4.266-11.211-4.264-15.475,0.003L32.09,24.435c-1.095,1.095-1.095,2.871,0,3.966S34.96,29.496,36.054,28.4z"
  //"M14.665,17.95c-0.909-0.906-0.909-2.38,0-3.286c0.907-0.909,2.38-0.909,3.287,0l5.675,5.671c0.909,0.906,0.909,2.38,0,3.286c-0.455,0.455-1.05,0.683-1.645,0.683s-1.189-0.228-1.645-0.683L14.665,17.95z M27.407,22.054h0.002c1.284-0.002,2.325-1.043,2.325-2.327l-0.007-8.018c-0.002-1.284-1.043-2.322-2.324-2.322H27.4c-1.284,0-2.324,1.042-2.324,2.327l0.006,8.018C25.085,21.013,26.125,22.054,27.407,22.054z M11.712,29.733h8.018c1.285,0,2.325-1.043,2.325-2.327c0-1.282-1.04-2.325-2.325-2.325h-8.018c-1.284,0-2.324,1.043-2.324,2.325C9.388,28.69,10.428,29.733,11.712,29.733z M51.669,48.381c-0.905-0.906-2.376-0.911-3.287,0c-0.909,0.904-0.909,2.38,0,3.286l5.666,5.676c0.455,0.454,1.05,0.681,1.645,0.681s1.19-0.227,1.645-0.681c0.909-0.905,0.909-2.38,0-3.289L51.669,48.381z M60.296,42.273l-8.023-0.002c-1.284,0-2.325,1.04-2.325,2.324s1.041,2.325,2.325,2.325l8.023,0.002c1.282,0,2.325-1.04,2.325-2.324C62.621,43.313,61.578,42.273,60.296,42.273z M44.604,49.946c-0.004,0-0.004,0-0.004,0c-1.284,0-2.325,1.038-2.325,2.322l-0.007,8.022c0,1.286,1.039,2.329,2.323,2.329c0.002,0,0.002,0,0.002,0c1.284,0,2.327-1.038,2.327-2.322l0.004-8.024C46.924,50.986,45.886,49.946,44.604,49.946z M36.099,39.463c-1.659,0.424-2.661,2.114-2.234,3.774c0.63,2.461-0.04,4.97-1.794,6.72l-5.152,5.155c-1.34,1.337-3.12,2.076-5.017,2.076c-1.895,0-3.673-0.737-5.013-2.081c-1.341-1.335-2.078-3.115-2.076-5.01c0-1.893,0.737-3.678,2.076-5.015l5.157-5.156c1.748-1.752,4.259-2.415,6.718-1.79c1.662,0.433,3.349-0.572,3.774-2.232c0.424-1.659-0.575-3.348-2.233-3.773c-4.614-1.184-9.339,0.097-12.645,3.416l-5.155,5.152c-2.51,2.51-3.893,5.847-3.893,9.398c-0.002,3.55,1.381,6.892,3.893,9.398c2.507,2.51,5.844,3.893,9.396,3.893c0,0,0.002,0,0.004,0c3.548,0,6.888-1.383,9.396-3.893l5.15-5.15c3.321-3.313,4.6-8.039,3.418-12.647C39.446,40.036,37.756,39.033,36.099,39.463z M54.343,36.455l5.154-5.152c5.184-5.186,5.184-13.62,0-18.799c-2.507-2.51-5.849-3.893-9.398-3.893c-3.552,0-6.889,1.383-9.398,3.893l-5.152,5.152c-3.317,3.313-4.595,8.042-3.416,12.647c0.424,1.658,2.105,2.663,3.771,2.235c1.657-0.424,2.658-2.114,2.235-3.771c-0.631-2.461,0.041-4.975,1.793-6.725l5.153-5.155c2.676-2.676,7.35-2.681,10.028,0.003c2.764,2.762,2.764,7.262,0,10.028l-5.157,5.154c-1.747,1.75-4.257,2.42-6.718,1.789c-1.651-0.435-3.351,0.575-3.774,2.235c-0.423,1.657,0.575,3.348,2.235,3.771c1.129,0.288,2.266,0.431,3.388,0.431C48.538,40.3,51.844,38.958,54.343,36.455z"
);

// utilities

const processUrl = (url: string) => {
  return url.replace(/^https?/, "").replace(/[?#].*$/, "");
};

const preprocessUrlMap = (urlMap: Record<string, string>) => {
  const newUrlMap = Object.create(null) as Record<string, string>;
  for (const url of Object.keys(urlMap)) {
    newUrlMap[processUrl(url)] = urlMap[url];
  }
  return newUrlMap;
};

const userName = (
  user: string | undefined,
  userMap: Record<string, User>
): React.ReactNode => {
  const userObj = userMap[user ?? ""];
  return userObj
    ? `${userObj.firstName} ${userObj.lastName}`
    : user === "none"
      ? "Nobody"
      : user === "null"
        ? "N/A"
        : "<Unknown>";
};

const preserveSpacing = (text: string) => text.replace(/ {2}/g, "\xa0 ");

const getDocumentURL = (apiUrl: string, tenantId: string, documentId: string) =>
  `${apiUrl}/v1/publicTenants/${tenantId}/documents/${documentId}/download`;

// context

const AgendaDocumentContext = createContext<AgendaDocumentContextType | null>(
  null
);
const useAgendaDocumentContext = (): AgendaDocumentContextType => {
  const context = useContext(AgendaDocumentContext);
  if (!context) {
    throw new Error("useAgendaDocument called outside context provider.");
  }
  return context;
};

const columnify = <T extends object>(data: T[], columns: number): T[][] => {
  const colSize = data.length / columns;
  const output: T[][] = [];
  for (let i = 0; i < columns; i++) {
    const from = Math.round(colSize * i);
    const to = Math.round(colSize * (i + 1));
    output.push(data.slice(from, to));
  }
  return output;
};

// #endregion

// #region Components

interface LetterheadLogoProps {
  url: string | undefined;
}
/** Letterhead logo */
const LetterheadLogoBlock = ({ url }: LetterheadLogoProps) => {
  return url ? (
    <Image src={url} style={{ aspectRatio: "1:1", width: 100, height: 100 }} />
  ) : null;
};

interface LetterheadOfficerBlockProps {
  member: TeamMemberWContact;
}
/** Letterhead officer block */
const LetterheadOfficerBlock = ({ member }: LetterheadOfficerBlockProps) => {
  return (
    <View
      style={{
        fontSize: 8,
        marginTop: 20,
        textAlign: "center",
        width: "100%",
      }}
    >
      <Text>
        {member.contactInfo.firstName} {member.contactInfo.lastName}
      </Text>
      <Text>{member.title}</Text>
      <Text>{arrayify(member.contactInfo.primaryPhone)[0]?.number}</Text>
      <Text>{member.contactInfo.email}</Text>
    </View>
  );
};

interface LetterheadOfficerNameProps {
  member: TeamMemberWContact;
}
/** Letterhead officer name */
const LetterheadOfficerName = ({ member }: LetterheadOfficerNameProps) => {
  return (
    <Text style={{ marginTop: 2 }}>
      {member.contactInfo.firstName} {member.contactInfo.lastName}
      {member.title ? `, ${member.title}` : null}
    </Text>
  );
};

interface LetterheadDetailsBlockProps {
  letterHead: LetterHead;
  address?: Address;
}
/** Letterhead details heading */
const LetterheadDetailsBlock = ({
  letterHead,
  address,
}: LetterheadDetailsBlockProps) => {
  return (
    <View
      style={{
        flexDirection: "column",
        alignItems: "center",
        textAlign: "center",
        marginBottom: 24,
      }}
    >
      <Text style={{ fontSize: 16, fontWeight: "bold", marginBottom: 8 }}>
        {letterHead.name}
      </Text>

      {address && (
        <>
          {address.street ? (
            <Text style={{ fontSize: "10px" }}>{address.street}</Text>
          ) : null}

          {address.street2 ? (
            <Text style={{ fontSize: "10px" }}>{address.street2}</Text>
          ) : null}

          {address.city || address.state || address.zip ? (
            <Text style={{ fontSize: "10px" }}>
              {(address.city ? `${address.city}, ` : "") +
                `${address.state} ${address.zip}`}
            </Text>
          ) : null}
        </>
      )}

      {letterHead.email ? (
        <Text style={{ fontSize: "10px" }}>{`Email: ${letterHead.email}`}</Text>
      ) : null}

      {letterHead.website ? (
        <Text
          style={{ fontSize: "10px" }}
        >{`Website: ${letterHead.website}`}</Text>
      ) : null}
    </View>
  );
};

interface MeetingDetailsBlockProps {
  showAddress?: boolean;
}
/** Meeting details heading */
const MeetingDetailsBlock = ({
  showAddress = true,
}: MeetingDetailsBlockProps) => {
  const { meeting, timeZone } = useAgendaDocumentContext();
  const address = showAddress ? meeting?.address : undefined;

  return (
    <>
      <View
        style={{
          fontSize: 24,
          textAlign: "center",
        }}
      >
        <Text>{meeting ? meeting.name : "{Meeting Name}"}</Text>
      </View>

      <View style={styles.meetingDate}>
        <Text style={{ textAlign: "center", marginTop: 4 }}>
          {meeting
            ? formatTZ(meeting.startTime, "MMMM dd, yyyy", timeZone)
            : "MMMM dd, yyyy"}{" "}
          at{" "}
          {meeting ? formatTZ(meeting.startTime, "h:mm a", timeZone) : "h:mm a"}
        </Text>

        {address && (
          <>
            <Text style={{ fontSize: "10px", marginTop: 4 }}>Held at:</Text>
            {address.street ? (
              <Text style={{ fontSize: "10px" }}>{address.street}</Text>
            ) : null}
            {address.street2 ? (
              <Text style={{ fontSize: "10px" }}>{address.street2}</Text>
            ) : null}
            {address.city || address.state || address.zip ? (
              <Text style={{ fontSize: "10px" }}>
                {(address.city ? `${address.city}, ` : "") +
                  `${address.state} ${address.zip}`}
              </Text>
            ) : null}
          </>
        )}

        <Text style={{ textAlign: "center", marginTop: 4 }}>
          {meeting ? meeting.description : "{Meeting Description}"}
        </Text>
      </View>
    </>
  );
};

interface DocumentBlockProps {
  document: Document;
  sectionIndex: number;
  itemIndex: number;
  index: number;
}
/** Render document */
const DocumentBlock = ({
  document: doc,
  sectionIndex,
  itemIndex,
  index,
}: DocumentBlockProps) => {
  const {
    enableLinks,
    substituteLinkMap,
    apiUrl,
    tenant,
    theme,
    sectionNumbering,
    itemNumbering,
    documentNumbering,
    nestNumbering,
  } = useAgendaDocumentContext();

  if (!(doc.visibleOnAgenda ?? true)) return null;

  const numbering = formatLineIndex({
    index: [sectionIndex, itemIndex, index],
    type: [sectionNumbering, itemNumbering, documentNumbering],
    nested: nestNumbering,
  });

  const attachment = doc.linkedAttachment
    ? doc.attachments.find(
        (attachment) => attachment.attachmentId === doc.linkedAttachment
      )
    : undefined;

  const spaceFromIcon = theme.documentIcon ? 4 : 0;

  return (
    <View
      style={{
        ...getStyles(theme, theme.documentTitle),
        flexDirection: "row",
        alignItems: "center",
      }}
    >
      {!!numbering && <Text>{numbering}</Text>}
      {theme.documentIcon ? (
        doc.confidential ? (
          <IconConfidential {...getIconStyles(theme.documentIcon)} />
        ) : !attachment?.url ? (
          <IconBrokenLink {...getIconStyles(theme.documentIcon)} />
        ) : (
          <IconAttachment {...getIconStyles(theme.documentIcon)} />
        )
      ) : null}

      {(doc.confidential && enableLinks !== "all") || !attachment?.url ? (
        <Text style={{ marginLeft: spaceFromIcon }}>{doc.name}</Text>
      ) : (
        <Link
          src={
            enableLinks && tenant
              ? substituteLinkMap[processUrl(attachment.url)] ??
                getDocumentURL(apiUrl, tenant?.id, doc.id)
              : "#"
          }
          style={{ marginLeft: spaceFromIcon }}
        >
          {doc.name}
        </Link>
      )}
    </View>
  );
};

interface AgendaItemBlockProps {
  agendaItem: AgendaItem;
  sectionIndex: number;
  index: number;
}
/** Render agenda item */
const AgendaItemBlock = ({
  agendaItem: item,
  sectionIndex,
  index,
}: AgendaItemBlockProps) => {
  const {
    augmented,
    userMap,
    sectionNumbering,
    itemNumbering,
    nestNumbering,
    theme,
  } = useAgendaDocumentContext();

  const numbering = formatLineIndex({
    index: [sectionIndex, index],
    type: [sectionNumbering, itemNumbering],
    nested: nestNumbering,
  });

  const augmentStyle: StyleProperties = getStyles(theme, theme.itemAugmented);
  const augmentMarginBottom = augmentStyle.marginBottom;
  augmentStyle.marginBottom = undefined;

  return (
    <>
      {/* Slice here works around https://github.com/diegomura/react-pdf/issues/3404 */}
      <Text style={getStyles(theme, theme.itemTitle)} minPresenceAhead={48}>
        <Text>{numbering.slice(0, 1)}</Text>
        {numbering.slice(1)}
        {item.name}
      </Text>

      {!!item.description && (
        <Text style={getStyles(theme, theme.itemDescription)}>
          {preserveSpacing(item.description)}
        </Text>
      )}

      {augmented && (
        <>
          {!!item.comment && (
            <Text style={augmentStyle}>
              <Text style={{ fontWeight: "bold" }}>Staff Comment:</Text>{" "}
              {preserveSpacing(item.comment)}
            </Text>
          )}
          {!!item.script && (
            <Text style={augmentStyle}>
              <Text style={{ fontWeight: "bold" }}>Script:</Text>{" "}
              {preserveSpacing(item.script)}
            </Text>
          )}
          {!!item.presenter &&
            (typeof item.presenter === "string" ||
              item.presenter.userId !== "none") &&
            userMap && (
              <Text style={augmentStyle}>
                <Text style={{ fontWeight: "bold" }}>Presenter:</Text>{" "}
                {typeof item.presenter === "string"
                  ? userName(item.presenter, userMap)
                  : item.presenter.manualEntry ??
                    userName(item.presenter.userId, userMap)}
              </Text>
            )}
          {!!item.motion && (
            <Text style={augmentStyle}>
              <Text style={{ fontWeight: "bold" }}>Motion:</Text>{" "}
              {preserveSpacing(item.motion)}
            </Text>
          )}
          {!!(item.comment || item.script || item.presenter || item.motion) && (
            <View
              style={{ width: "100%", marginBottom: augmentMarginBottom }}
            />
          )}
        </>
      )}

      {item.documents?.map((doc, i) => (
        <DocumentBlock
          key={doc.id}
          document={doc}
          sectionIndex={sectionIndex}
          itemIndex={index}
          index={i}
        />
      ))}
    </>
  );
};

interface AgendaModuleBlockProps {
  agendaModule: AgendaModule;
  index: number;
}
/** Render agenda module */
const AgendaModuleBlock = ({
  agendaModule: module,
  index,
}: AgendaModuleBlockProps) => {
  const { sectionNumbering, nestNumbering, theme } = useAgendaDocumentContext();

  const numbering = formatLineIndex({
    index: [index],
    type: [sectionNumbering],
    nested: nestNumbering,
  });

  return (
    <>
      {/* Slice here works around https://github.com/diegomura/react-pdf/issues/3404 */}
      <Text minPresenceAhead={48} style={getStyles(theme, theme.sectionTitle)}>
        <Text>{numbering.slice(0, 1)}</Text>
        {numbering.slice(1)}
        {module.name}
      </Text>

      {!!module.description && (
        <Text style={getStyles(theme, theme.sectionDescription)}>
          {preserveSpacing(module.description)}
        </Text>
      )}

      {module.agendaItems?.map((item, i) => (
        <AgendaItemBlock
          key={item.id}
          agendaItem={item}
          sectionIndex={index}
          index={i}
        />
      ))}
    </>
  );
};

const AgendaBlock = () => {
  const { meeting } = useAgendaDocumentContext();

  return (
    <View style={{ padding: 15 }}>
      {meeting?.agenda ? (
        meeting.agenda.agendaModules?.map((module, index) => (
          <AgendaModuleBlock
            key={module.id}
            agendaModule={module}
            index={index}
          />
        ))
      ) : (
        <Text style={{ textAlign: "center" }}>(No agenda attached)</Text>
      )}
    </View>
  );
};

// #endregion

// #region Layouts

const LayoutSidebar = () => {
  const { letterHead, team } = useAgendaDocumentContext();

  return (
    <View style={{ flexDirection: "row" }}>
      {!!letterHead && (
        <View
          style={{
            width: "30%",
            flexDirection: "column",
            alignItems: "center",
          }}
        >
          <LetterheadLogoBlock url={letterHead.logoUrl} />

          <View style={{ marginBottom: 20 }} />

          <LetterheadDetailsBlock
            letterHead={letterHead}
            address={team?.address}
          />

          <Text style={{ fontSize: "10px", fontWeight: "bold" }}>
            Officials:
          </Text>

          {letterHead.members.map((member, i) => (
            <LetterheadOfficerBlock key={i} member={member} />
          ))}
        </View>
      )}

      <View style={{ flexBasis: 0, flexGrow: 1 }}>
        <View style={{ marginTop: 10 }} />

        <MeetingDetailsBlock />

        <AgendaBlock />
      </View>
    </View>
  );
};

const LayoutTopAcross = () => {
  const { letterHead } = useAgendaDocumentContext();

  return (
    <View>
      <View
        style={{
          flexDirection: "row",
          justifyContent: "space-between",
          alignItems: "center",
        }}
      >
        <View
          style={{ width: 150, flexDirection: "row", justifyContent: "center" }}
        >
          <LetterheadLogoBlock url={letterHead?.logoUrl} />
        </View>

        <View style={{ flexBasis: 0, flexGrow: 1 }}>
          <MeetingDetailsBlock />
        </View>

        <View style={{ fontSize: 8, flexBasis: 150, textAlign: "center" }}>
          <Text style={{ fontSize: 9, fontWeight: "bold" }}>
            Board Members:
          </Text>
          {letterHead?.members.map((member, i) => (
            <LetterheadOfficerName key={i} member={member} />
          ))}
        </View>
      </View>

      <AgendaBlock />
    </View>
  );
};

const LayoutTopCenter = () => {
  const { letterHead } = useAgendaDocumentContext();

  const members = letterHead?.members ?? [];
  const columnCount = 2;
  const columns = columnify(members, columnCount);

  return (
    <View>
      <View
        style={{
          width: "100%",
          paddingHorizontal: 15,
          flexDirection: "column",
          alignItems: "center",
        }}
      >
        <LetterheadLogoBlock url={letterHead?.logoUrl} />

        <MeetingDetailsBlock />

        <View
          style={{
            width: "100%",
            flexDirection: "row",
            justifyContent: "space-between",
            marginTop: 10,
            alignItems: "flex-start",
            paddingBottom: 10,
            borderBottom: 1,
            borderBottomColor: "black",
            borderBottomWidth: 1,
          }}
        >
          {columns.map((column, i) => (
            <View key={i}>
              {column.map((member, j) => (
                <LetterheadOfficerName key={j} member={member} />
              ))}
            </View>
          ))}
        </View>
      </View>

      <AgendaBlock />
    </View>
  );
};

const LayoutTopAcrossBoard = () => {
  const { letterHead, meeting, team } = useAgendaDocumentContext();

  const address = meeting?.address ?? team?.address;
  const heldAt = !!meeting?.address;

  const members = letterHead?.members ?? [];
  const columnCount = 2;
  const columns = columnify(members, columnCount);

  return (
    <View>
      <View
        style={{
          flexDirection: "row",
          justifyContent: "space-between",
          alignItems: "center",
        }}
      >
        <View
          style={{ width: 150, flexDirection: "row", justifyContent: "center" }}
        >
          <LetterheadLogoBlock url={letterHead?.logoUrl} />
        </View>

        <View style={{ flexBasis: 0, flexGrow: 1 }}>
          <MeetingDetailsBlock showAddress={false} />
        </View>

        <View style={{ fontSize: 8, flexBasis: 150, textAlign: "center" }}>
          {address && (
            <>
              {heldAt && (
                <Text style={{ fontSize: "10px", marginTop: 4 }}>Held at:</Text>
              )}
              {address.street ? (
                <Text style={{ fontSize: "10px" }}>{address.street}</Text>
              ) : null}
              {address.street2 ? (
                <Text style={{ fontSize: "10px" }}>{address.street2}</Text>
              ) : null}
              {address.city || address.state || address.zip ? (
                <Text style={{ fontSize: "10px" }}>
                  {(address.city ? `${address.city}, ` : "") +
                    `${address.state} ${address.zip}`}
                </Text>
              ) : null}
            </>
          )}
        </View>
      </View>

      <View
        style={{
          width: "100%",
          flexDirection: "row",
          justifyContent: "space-between",
          marginTop: 10,
          alignItems: "flex-start",
          paddingBottom: 10,
          borderBottom: 1,
          borderBottomColor: "black",
          borderBottomWidth: 1,
        }}
      >
        {columns.map((column, i) => (
          <View key={i}>
            {column.map((member, j) => (
              <LetterheadOfficerName key={j} member={member} />
            ))}
          </View>
        ))}
      </View>

      <AgendaBlock />
    </View>
  );
};

// #endregion

// #region Root

export const AgendaDocument = Object.assign(
  ({
    tenant,
    meeting,
    team,
    letterHead,
    enableLinks = true,
    apiUrl,
    substituteLinkMap = {},
    userMap,
    augmented = false,
  }: AgendaPreviewProps) => {
    if (!fontsLoaded) throw new Error("Fonts not loaded");

    substituteLinkMap = preprocessUrlMap(substituteLinkMap ?? {});
    console.log(substituteLinkMap);

    const theme = coerceTheme(letterHead?.theme);
    const layout = coerceLayout(letterHead?.layout);

    const sectionNumbering =
      letterHead?.sectionNumbering ?? tenant?.numberFormat ?? "none";
    const itemNumbering = letterHead?.itemNumbering ?? "none";
    const nestNumbering = letterHead?.nestNumbering ?? false;

    return (
      <PDFDocument title={meeting?.name ?? "Meeting"}>
        <AgendaDocumentContext.Provider
          value={{
            tenant,
            meeting,
            team,
            letterHead,
            enableLinks,
            apiUrl,
            substituteLinkMap,
            userMap,
            augmented,
            sectionNumbering,
            itemNumbering,
            documentNumbering: "none",
            nestNumbering,
            // additional
            timeZone: tenant?.timeZone ?? "America/New_York",
            theme,
            layout,
          }}
        >
          <Page size="LETTER" style={styles.page}>
            {layout === "sidebar" ? (
              <LayoutSidebar />
            ) : layout === "topAcross" ? (
              <LayoutTopAcross />
            ) : layout === "topCenter" ? (
              <LayoutTopCenter />
            ) : layout === "topAcrossBoard" ? (
              <LayoutTopAcrossBoard />
            ) : null}
          </Page>
        </AgendaDocumentContext.Provider>
      </PDFDocument>
    );
  },
  { readyPromise }
);

// #endregion
